<template>
  <section class="contacts-table-wrap">
    <!-- Barra de seleção em massa -->
    <div v-if="selectContactsLength > 0" class="bulk">
      <label class="bulk-check">
        <input
          type="checkbox"
          :checked="allContactsSelected"
          :indeterminate.prop="!allContactsSelected"
          @change="handleSelectAll($event)"
        />
      </label>
      <b>{{
        $t('BULK_ACTION.CONTACTS_SELECTED', {
          contactsCount: selectContactsLength,
        })
      }}</b>
      <div class="bsep" />
      <button class="bulk-clear" @click="clearSelection">
        {{ $t('CONTACTS_PAGE.LIST.CLEAR') || 'Limpar' }}
      </button>
    </div>

    <div class="tbl-wrap" @mouseleave="onCardLeave">
      <table class="tbl">
        <thead>
          <tr>
            <th class="col-chk">
              <input
                type="checkbox"
                :checked="allContactsSelected && contacts.length > 0"
                :indeterminate.prop="
                  selectContactsLength > 0 && !allContactsSelected
                "
                @change="handleSelectAll($event)"
              />
            </th>
            <th class="sortable" @click="toggleSort('name')">
              {{ $t('CONTACTS_PAGE.LIST.TABLE_HEADER.NAME') }}
              <span class="sort-caret" :class="caretClass('name')" />
            </th>
            <th>{{ $t('CONTACTS_PAGE.LIST.TABLE_HEADER.PHONE_NUMBER') }}</th>
            <th>{{ $t('CONTACTS_PAGE.LIST.TABLE_HEADER.COMPANY') }}</th>
            <th class="sortable" @click="toggleSort('last_activity_at')">
              {{ $t('CONTACTS_PAGE.LIST.TABLE_HEADER.LAST_ACTIVITY') }}
              <span
                class="sort-caret"
                :class="caretClass('last_activity_at')"
              />
            </th>
            <th class="col-num">
              {{ $t('CONTACTS_PAGE.LIST.TABLE_HEADER.CONVERSATIONS') }}
            </th>
            <th class="col-act" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in tableData"
            :key="row.id"
            :class="{ 'is-active': row.id === activeContactId }"
          >
            <td class="col-chk">
              <input
                type="checkbox"
                :checked="isSelected(row.id)"
                @change="handleSelected(row.id)"
              />
            </td>
            <td>
              <div class="cc" @click="onClickContact(row.id)">
                <thumbnail
                  :src="row.thumbnail"
                  size="36px"
                  :username="row.name"
                  :status="row.availability_status"
                />
                <div class="cc-meta">
                  <router-link
                    class="cc-name"
                    :to="contactPath(row.id)"
                    @click.native.stop
                  >
                    {{ row.name }}
                  </router-link>
                  <span v-if="row.email" class="cc-mail">{{ row.email }}</span>
                  <span v-else class="cc-mail cc-muted">{{
                    $t('CONTACTS_PAGE.LIST.VIEW_DETAILS')
                  }}</span>
                </div>
              </div>
            </td>
            <td class="phone">
              <span v-if="row.hasPhone">{{ row.phone_number }}</span>
              <span v-else class="muted">—</span>
            </td>
            <td>
              <span v-if="row.companyName" class="company-chip">{{
                row.companyName
              }}</span>
              <span v-else class="muted">—</span>
            </td>
            <td>
              <span v-if="row.hasActivity" class="last">
                <span class="last-dot" />
                {{ row.last_activity_at }}
              </span>
              <span v-else class="muted">—</span>
            </td>
            <td class="col-num">
              <span v-if="row.convCount" class="conv-badge">{{
                row.convCount
              }}</span>
              <span v-else class="muted">0</span>
            </td>
            <td class="col-act">
              <div class="row-actions">
                <woot-button
                  v-tooltip.left="$t('CONTACTS_PAGE.LIST.NEW_CONVERSATION')"
                  class="act-chat"
                  variant="clear"
                  size="small"
                  :is-loading="loadingNewConversation[row.id] || false"
                  @click="onClickNewChat(row)"
                >
                  <fluent-icon icon="chat" size="16" />
                </woot-button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <empty-state
        v-if="showSearchEmptyState"
        :title="$t('CONTACTS_PAGE.LIST.404')"
      />
      <empty-state
        v-else-if="!isLoading && !contacts.length"
        :title="$t('CONTACTS_PAGE.LIST.NO_CONTACTS')"
      />
      <div v-if="isLoading" class="contacts--loader">
        <spinner />
        <span>{{ $t('CONTACTS_PAGE.LIST.LOADING_MESSAGE') }}</span>
      </div>
    </div>

    <new-conversation
      v-if="showConversationModal"
      :show="showConversationModal"
      :contact-conversations="contactConversations"
      :contact="contactSelected"
      @success="closeConversationModal"
      @cancel="toggleConversationModal"
    />
  </section>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

import Spinner from 'shared/components/Spinner.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon';
import timeMixin from 'dashboard/mixins/time';
import rtlMixin from 'shared/mixins/rtlMixin';
import alertMixin from 'shared/mixins/alertMixin';
import NewConversation from '../../conversation/contact/NewConversation.vue';
import { mapGetters } from 'vuex';
import { frontendURL } from '../../../../helper/URLHelper';
import agentMixin from '../../../../mixins/agentMixin';

export default {
  components: {
    EmptyState,
    Spinner,
    Thumbnail,
    FluentIcon,
    NewConversation,
  },
  mixins: [alertMixin, clickaway, timeMixin, rtlMixin, agentMixin],
  props: {
    contacts: {
      type: Array,
      default: () => [],
    },
    showSearchEmptyState: {
      type: Boolean,
      default: false,
    },
    onClickContact: {
      type: Function,
      default: () => {},
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    activeContactId: {
      type: [String, Number],
      default: '',
    },
    sortParam: {
      type: String,
      default: 'last_activity_at',
    },
    sortOrder: {
      type: String,
      default: 'desc',
    },
    isContactsDelete: {
      type: Boolean,
      default: false,
    },
    selectedContacts: {
      type: Array,
      default: () => [],
    },
    selectAllContacts: {
      type: Function,
      default: () => {},
    },
    allContactsSelected: {
      type: Boolean,
      default: false,
    },
    selectContactsLength: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      sortConfig: {},
      contactSelected: null,
      showConversationModal: false,
      checkedId: '',
      loadingNewConversation: {},
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      activeInbox: 'getSelectedInbox',
      currentUserID: 'getCurrentUserID',
      inboxes: 'inboxes/getInboxes',
      currentUser: 'getCurrentUser',
    }),
    verifyContacts() {
      return this.isContactsDelete;
    },
    contactConversations() {
      const currentId = this.currentUser?.id;
      const currentConversations = this.$store.getters[
        'contactConversations/getContactConversation'
      ](
        this.contactId,
        currentId,
        this.isLimitedAgent,
        !this.isPermissionSeeAllTeamConversation
      );
      return currentConversations;
    },
    tableData() {
      if (this.isLoading) {
        return [];
      }
      return this.contacts
        .filter(item => item)
        .filter(item => !`${item.phone_number || ''}`.includes('@g.us'))
        .map(item => {
          const additional = item?.additional_attributes || {};
          const companyName =
            item.company?.name || additional.company_name || '';
          const lastActivityAt = item.last_activity_at;
          return {
            ...item,
            hasPhone: !!item.phone_number,
            companyName,
            convCount: item.conversations_count || 0,
            hasActivity: !!lastActivityAt,
            last_activity_at: lastActivityAt
              ? this.dynamicTime(lastActivityAt)
              : '',
          };
        });
    },
  },
  watch: {
    sortOrder() {
      this.setSortConfig();
    },
    sortParam() {
      this.setSortConfig();
    },
  },
  mounted() {
    if (this.$route.name.includes('contact')) {
      this.$store.dispatch('setActiveInbox', null);
    }
    this.setSortConfig();
  },
  methods: {
    contactPath(id) {
      return frontendURL(`accounts/${this.accountId}/contacts/${id}`);
    },
    caretClass(field) {
      if (this.sortConfig[field] === 'asc') return 'asc';
      if (this.sortConfig[field] === 'desc') return 'desc';
      return '';
    },
    toggleSort(field) {
      const current = this.sortConfig[field];
      const next = current === 'desc' ? 'asc' : 'desc';
      this.$emit('on-sort-change', { [field]: next });
    },
    handleSelected(id) {
      return this.$emit('select-contacts', id);
    },
    clearSelection() {
      this.$emit('select-all-contacts', { target: { checked: false } });
    },
    onCardLeave() {
      if (this.verifyContacts) {
        this.checkedId = '';
      }
    },
    isSelected(id) {
      return this.selectedContacts.includes(id);
    },
    handleSelectAll(e) {
      this.$emit('select-all-contacts', e);
    },
    setSortConfig() {
      this.sortConfig = { [this.sortParam]: this.sortOrder };
    },
    async onClickNewChat(contact) {
      try {
        this.$set(this.loadingNewConversation, contact.id, true);
        this.contactId = contact?.id;
        await this.$store.dispatch(
          'contacts/fetchContactableInbox',
          contact?.id
        );
        this.toggleConversationModal();
        const contactState = this.$store.getters['contacts/getContact'](
          contact.id
        );
        this.contactSelected = { ...contact, ...contactState };
        await this.fetchData(contact);
      } catch {
        this.showAlert(this.$t('CONTACT_PANEL.ERROR_OPEN_NEW_CONVERSATION'));
      } finally {
        this.$set(this.loadingNewConversation, contact.id, false);
      }
      this.showConversationModal = true;
    },
    async fetchData(contact) {
      await this.$store.dispatch('contactConversations/fetch', contact?.id);
    },
    closeConversationModal() {
      this.$emit('close-modal-contact');
      this.toggleConversationModal();
    },
    toggleConversationModal() {
      this.showConversationModal = !this.showConversationModal;
    },
  },
};
</script>

<style lang="scss" scoped>
.contacts-table-wrap {
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 2.4rem 2.4rem;
}

/* barra de seleção navy */
.bulk {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #002136;
  color: #fff;
  border-radius: 1.1rem;
  padding: 0.8rem 1rem 0.8rem 1.4rem;
  margin-bottom: 1.2rem;

  b {
    font-size: 1.3rem;
    font-weight: 600;
  }
  .bsep {
    flex: 1;
  }
  .bulk-check {
    display: flex;
    margin: 0;
  }
  .bulk-check input {
    margin: 0;
    accent-color: #01c6d8;
    width: 1.6rem;
    height: 1.6rem;
    cursor: pointer;
  }
  .bulk-clear {
    font-size: 1.25rem;
    font-weight: 600;
    color: #cfe3e8;
    padding: 0.6rem 1rem;
    border-radius: 0.8rem;
    background: transparent;
    border: 0;
    cursor: pointer;
    transition: 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
      color: #fff;
    }
  }
}

/* tabela */
.tbl-wrap {
  flex: 1 1 0%;
  min-height: 0;
  overflow: auto;
  background: var(--white);
  border: 1px solid #e4e9ee;
  border-radius: 1.4rem;
}

table.tbl {
  width: 100%;
  border-collapse: collapse;
}

.tbl th {
  position: sticky;
  top: 0;
  z-index: 2;
  text-align: left;
  font-size: 1.1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04rem;
  color: #8a97a3;
  padding: 1.1rem 1.4rem;
  background: #f0f3f5;
  border-bottom: 1px solid #e4e9ee;
  white-space: nowrap;
  user-select: none;
}

.tbl th.sortable {
  cursor: pointer;
}
.tbl th.sortable:hover {
  color: #516170;
}
.sort-caret {
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 0.4rem;
  vertical-align: middle;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  opacity: 0;
}
.sort-caret.asc {
  border-bottom: 5px solid #0a7581;
  opacity: 1;
}
.sort-caret.desc {
  border-top: 5px solid #0a7581;
  opacity: 1;
}

.tbl td {
  padding: 1.1rem 1.4rem;
  border-bottom: 1px solid #eef2f5;
  font-size: 1.3rem;
  vertical-align: middle;
  color: #0f1f2a;
}
.tbl tbody tr:last-child td {
  border-bottom: none;
}
.tbl tbody tr {
  transition: background 0.12s;
}
.tbl tbody tr:hover {
  background: #f6f8fa;
}
.tbl tbody tr.is-active {
  background: #e1f8fb;
}

.col-chk {
  width: 4.4rem;
  text-align: center;
}
.col-chk input {
  margin: 0;
  accent-color: #01c6d8;
  width: 1.6rem;
  height: 1.6rem;
  cursor: pointer;
  vertical-align: middle;
}
.col-num {
  width: 11rem;
}
.col-act {
  width: 7rem;
  text-align: center;
}

.cc {
  display: flex;
  align-items: center;
  gap: 1.1rem;
  min-width: 0;
  cursor: pointer;
}
.cc-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.cc-name {
  font-weight: 650;
  font-size: 1.3rem;
  color: #002136 !important;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  text-decoration: none;
  text-transform: capitalize;
}
.cc:hover .cc-name {
  color: #0a7581 !important;
}
.cc-mail {
  font-size: 1.15rem;
  color: #8a97a3;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.cc-muted {
  font-style: italic;
  opacity: 0.7;
}

.phone {
  font-variant-numeric: tabular-nums;
  color: #516170;
}
.muted {
  color: #b6c0c9;
}

.company-chip {
  display: inline-block;
  max-width: 20rem;
  font-size: 1.2rem;
  color: #516170;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.last {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  color: #516170;
}
.last-dot {
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 50%;
  background: #01c6d8;
  flex: none;
}

.conv-badge {
  display: inline-grid;
  place-items: center;
  min-width: 2.4rem;
  height: 2.4rem;
  padding: 0 0.7rem;
  border-radius: 1.2rem;
  background: #e1f8fb;
  color: #0a7581;
  font-size: 1.2rem;
  font-weight: 700;
}

.row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}
.contacts-table-wrap::v-deep .act-chat {
  width: 3rem;
  height: 3rem;
  border-radius: 0.7rem;
  color: #0a7581;
  padding: 0;

  &:hover {
    background: #e1f8fb;
  }
}

.contacts--loader {
  align-items: center;
  display: flex;
  gap: 0.8rem;
  font-size: var(--font-size-default);
  justify-content: center;
  padding: var(--space-big);
}
</style>
